import styled, { keyframes } from "styled-components";

/* ═══════════════════════════════════════════════════════════════════════
   TermsModal — Light Water redesign
   Frosted-white welcome panel with logo lockup, gradient title, frosted
   notice cards, brand-aqua continue CTA. Matches EnterPromptModal shell.
   ═══════════════════════════════════════════════════════════════════════ */

const panelFloat = keyframes`
  0%, 100% { filter: brightness(1); }
  50%      { filter: brightness(1.02); }
`;

const fadeSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 1000;
  background:
    radial-gradient(
      circle at 50% 30%,
      rgba(var(--color-brand-400-rgb), 0.18),
      transparent 55%
    ),
    rgba(15, 47, 84, 0.32);
  backdrop-filter: blur(12px) saturate(140%);
  -webkit-backdrop-filter: blur(12px) saturate(140%);
  opacity: ${({ $isAnimating }) => ($isAnimating ? 1 : 0)};
  transition: opacity 220ms ease;
`;

export const ModalContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 420px;
  max-height: 90dvh;
  overflow-y: auto;
  border-radius: 28px;
  border: 1px solid var(--glass-edge);
  background:
    radial-gradient(
      120% 60% at 30% -10%,
      rgba(var(--glass-white-rgb), 0.96) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 55%,
      rgba(var(--glass-pale-rgb), 0.82) 100%
    ),
    linear-gradient(
      180deg,
      rgba(var(--glass-white-rgb), 0.94) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 100%
    );
  backdrop-filter: blur(12px) saturate(180%);
  -webkit-backdrop-filter: blur(12px) saturate(180%);
  box-shadow:
    0 32px 64px -18px rgba(var(--color-brand-500-rgb), 0.4),
    0 12px 28px rgba(var(--color-brand-400-rgb), 0.22),
    inset 0 2px 0 rgba(255, 255, 255, 0.95),
    inset 0 -10px 22px rgba(var(--color-brand-200-rgb), 0.26),
    inset 0 0 0 1px rgba(255, 255, 255, 0.45);
  isolation: isolate;
  opacity: ${({ $isAnimating }) => ($isAnimating ? 1 : 0)};
  transform: ${({ $isAnimating }) =>
    $isAnimating ? "scale(1)" : "scale(0.94) translateY(20px)"};
  transition:
    opacity 280ms ease,
    transform 280ms var(--ease-snappy);
  animation: ${panelFloat} 5.5s ease-in-out infinite;

  /* Bottom aqua underglow */
  &::after {
    content: "";
    position: absolute;
    bottom: -8px;
    left: 25%;
    right: 25%;
    height: 16px;
    background: radial-gradient(
      ellipse at center top,
      rgba(var(--color-brand-400-rgb), 0.42) 0%,
      rgba(var(--color-brand-400-rgb), 0) 70%
    );
    pointer-events: none;
    z-index: -1;
  }

  [data-theme="dark"] & {
    border: none;
    background: var(--color-surface-2);
    box-shadow:
      0 28px 56px -18px rgba(0, 0, 0, 0.6),
      0 10px 24px rgba(0, 0, 0, 0.4),
      inset 0 -10px 22px rgba(var(--color-brand-700-rgb), 0.18);
    animation: none;
  }

  [data-theme="dark"] &::after {
    opacity: 0;
  }
`;

export const ModalContent = styled.div`
  position: relative;
  z-index: 1;
  padding: 32px 26px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 22px;

  @media (max-width: 480px) {
    padding: 24px 18px 20px;
    gap: 18px;
  }
`;

export const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;

  svg {
    filter: drop-shadow(0 2px 6px rgba(var(--color-brand-500-rgb), 0.18));
  }
`;

export const ModalTitle = styled.h2`
  margin: 0;
  font-family: var(--font-family-primary);
  font-size: 26px;
  font-weight: 800;
  line-height: normal;
  letter-spacing: -0.03em;
  text-align: center;

  /* Brand gradient title */
  background: linear-gradient(
    0deg,
    var(--color-text-strong) 0%,
    var(--color-text-body) 45%,
    var(--color-brand-600) 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  filter: drop-shadow(0 1px 2px rgba(var(--color-brand-600-rgb), 0.14));
`;

export const NoticeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

export const NoticeItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 14px 16px;
  border-radius: 18px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-1);
  backdrop-filter: blur(18px) saturate(160%);
  -webkit-backdrop-filter: blur(18px) saturate(160%);
  box-shadow:
    var(--shadow-micro),
    inset 0 1px 0 rgba(255, 255, 255, 0.92);
  animation: ${fadeSlideIn} 0.35s ease-out both;
  animation-delay: ${({ $delay }) => $delay || "0s"};

  [data-theme="dark"] & {
    border: none;
    background: var(--color-surface-3);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.35);
  }
`;

export const NoticeEmoji = styled.span`
  font-size: 22px;
  line-height: normal;
  flex-shrink: 0;
`;

export const NoticeText = styled.span`
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 500;
  line-height: normal;
  letter-spacing: -0.005em;
`;

export const FooterText = styled.p`
  margin: 0;
  text-align: center;
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 500;
  line-height: normal;
`;

export const ContinueButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  max-width: 240px;
  height: 48px;
  padding: 0 22px;
  border-radius: 999px;
  border: 1px solid var(--glass-edge-soft);
  background: linear-gradient(
    180deg,
    var(--color-brand-300) 0%,
    var(--color-brand-500) 100%
  );
  color: var(--glass-text-on-pill);
  text-shadow: 0 1px 1px rgba(var(--color-brand-900-rgb), 0.22);
  box-shadow: var(--shadow-cta);
  font-family: var(--font-family-primary);
  font-size: 15px;
  font-weight: 800;
  letter-spacing: -0.005em;
  cursor: pointer;
  outline: none;
  overflow: hidden;
  transition:
    transform 180ms var(--ease-snappy),
    filter 180ms var(--ease-snappy);

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.05);
  }

  [data-theme="dark"] & {
    border: none;
  }
`;

export const LogoImg = styled.img`
  height: 42px;
  width: auto;
  display: block;
  filter: drop-shadow(0 6px 14px rgba(var(--color-brand-400-rgb), 0.3))
    drop-shadow(0 2px 4px rgba(var(--glass-white-rgb), 0.6));

  @media (max-width: 768px) {
    height: 34px;
  }
`;
